import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button, LoadingBlock, Notice, TextField } from '@wodi/ui';
import { formatEatDateTime } from '@wodi/domain';
import { useEffect, useState } from 'react';

import { inspectAccountLink, redeemAccountLink } from '../api/account-security.ts';
import { ApiError } from '../api/client.ts';
import { sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from './sign-in.module.css';

// A one-time setup or password reset link (ADR 0037): /account-link#<token>. The token is in
// the fragment so it never reaches a server log; it is taken off the address bar as soon as
// the page reads it, so it isn't left in the browser's history either.

const TOKEN = /^[A-Za-z0-9_-]{43}$/;
let taken: string | null = null;

/** The token from the address, once; later renders reuse it. */
function takeToken(): string | null {
  const fromAddress = window.location.hash.slice(1);
  if (TOKEN.test(fromAddress)) {
    taken = fromAddress;
    window.history.replaceState(window.history.state, '', window.location.pathname);
  }
  return taken;
}

function messageOf(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function Frame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className={styles.page}>
      <div className={styles.column}>
        <div className={styles.brand}>Wodi</div>
        <h1 className={styles.title}>{title}</h1>
        {children}
      </div>
    </main>
  );
}

const NEXT_STEP = {
  done: '/',
  two_factor: '/sign-in/code',
  two_factor_enrolment: '/sign-in/set-up-2fa',
} as const;

function PasswordForm({ token, email }: { token: string; email: string }) {
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const redeem = useMutation({
    mutationFn: () => redeemAccountLink(token, password),
    onSuccess: async ({ next }) => {
      taken = null;
      if (next === 'sign_in') return;
      await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
      await navigate({ to: NEXT_STEP[next], search: { next: '/' } });
    },
  });

  if (redeem.data?.next === 'sign_in') {
    return (
      <>
        <Notice tone="info">Your password is set.</Notice>
        <p className={styles.lead}>
          You don&apos;t have a role at any facility yet. Ask your administrator, then{' '}
          <Link to="/sign-in">sign in</Link>.
        </p>
      </>
    );
  }

  return (
    <form
      className={styles.form}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const mismatch =
          password === ''
            ? 'Enter a new password.'
            : password !== confirm
              ? 'The two passwords are different. Type them again.'
              : null;
        setProblem(mismatch);
        if (mismatch === null) redeem.mutate();
      }}
    >
      <p className={styles.lead}>
        For <strong>{email}</strong>. Use at least 12 characters: a few unrelated words work well.
        Common passwords are refused.
      </p>
      {online ? null : (
        <Notice tone="info">
          You&apos;re offline. Connect to the internet to set your password.
        </Notice>
      )}
      {redeem.error === null ? null : (
        <Notice tone="error">
          {messageOf(redeem.error, "Your password wasn't set. Try again.")}
        </Notice>
      )}
      <TextField
        label="New password"
        type="password"
        autoComplete="new-password"
        autoFocus
        value={password}
        onChange={setPassword}
      />
      <TextField
        label="Type it again"
        type="password"
        autoComplete="new-password"
        value={confirm}
        onChange={setConfirm}
        {...(problem === null ? {} : { errorMessage: problem })}
      />
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online}
          isPending={redeem.isPending}
          pendingLabel="Setting password…"
        >
          Set password
        </Button>
      </div>
    </form>
  );
}

export function AccountLinkPage() {
  const online = useOnline();
  const [token] = useState(takeToken);
  // The token lives in this page's state; leaving the page forgets it.
  useEffect(
    () => () => {
      taken = null;
    },
    [],
  );
  const link = useQuery({
    queryKey: ['account-link', token],
    queryFn: () => inspectAccountLink(token ?? ''),
    enabled: token !== null,
    retry: false,
    staleTime: Infinity,
  });

  if (token === null) {
    return (
      <Frame title="This link isn't complete">
        <Notice tone="error">
          Open the whole link you were given, or ask your administrator for a new one.
        </Notice>
      </Frame>
    );
  }
  if (link.isPending) {
    return (
      <Frame title="Checking your link">
        <LoadingBlock label="Checking your link" rows={2} />
      </Frame>
    );
  }
  if (link.isError) {
    const gone = link.error instanceof ApiError && link.error.code === 'link_not_valid';
    return (
      <Frame title={gone ? 'This link no longer works' : "Your link couldn't be checked"}>
        <Notice
          tone="error"
          {...(gone
            ? {}
            : {
                action: (
                  <Button dense isDisabled={!online} onPress={() => void link.refetch()}>
                    Try again
                  </Button>
                ),
              })}
        >
          {online
            ? messageOf(link.error, "Your link couldn't be checked. Try again.")
            : "You're offline. Connect to the internet, then try again."}
        </Notice>
        <p className={styles.lead}>
          Already have a password? <Link to="/sign-in">Sign in</Link>.
        </p>
      </Frame>
    );
  }
  const setup = link.data.purpose === 'setup';
  return (
    <Frame title={setup ? 'Set up your account' : 'Choose a new password'}>
      {setup ? (
        <p className={styles.lead}>
          Welcome, {link.data.displayName}. Choose a password, then set up two-factor sign-in if
          your role needs it.
        </p>
      ) : null}
      <PasswordForm token={token} email={link.data.email} />
      <p className={styles.lead}>
        This link works once, until {formatEatDateTime(new Date(link.data.expiresAt))}.
      </p>
    </Frame>
  );
}
